import { useEffect, useState } from 'react'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

interface LogEvent {
  id: string
  type: string
  createdAt: string
  label: string
  sub: string | null
  actorRole: string | null
}

const TYPE_CONFIG: Record<string, { icon: string; color: string }> = {
  company_signup:   { icon: '🏢', color: 'bg-accent/10 border-accent/20' },
  provider_signup:  { icon: '👤', color: 'bg-success/10 border-success/20' },
  mission_created:  { icon: '📋', color: 'bg-warning/10 border-warning/20' },
  mission_updated:  { icon: '✏️', color: 'bg-warning/10 border-warning/20' },
  payment:          { icon: '💳', color: 'bg-purple-500/10 border-purple-500/20' },
  assignment:       { icon: '🤝', color: 'bg-white/[0.06] border-white/[0.12]' },
  profile_updated:  { icon: '🔧', color: 'bg-white/[0.06] border-white/[0.12]' },
  password_changed: { icon: '🔑', color: 'bg-danger/10 border-danger/20' },
  deploy:           { icon: '🚀', color: 'bg-success/10 border-success/20' },
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

export default function LogsPage() {
  const [logs, setLogs] = useState<LogEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    fetch(`${API}/api/admin/logs?limit=100`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
    })
      .then((r) => r.json())
      .then((data) => { setLogs(Array.isArray(data) ? data : []); setLoading(false) })
  }, [])

  const types = [
    { key: 'all', label: 'Tout' },
    { key: 'company_signup', label: 'Inscriptions entreprises' },
    { key: 'provider_signup', label: 'Inscriptions prestataires' },
    { key: 'mission_created', label: 'Missions' },
    { key: 'payment', label: 'Paiements' },
    { key: 'assignment', label: 'Assignations' },
    { key: 'profile_updated', label: 'Mises à jour profil' },
    { key: 'password_changed', label: 'Changements mdp' },
    { key: 'deploy', label: 'Déploiements' },
  ]

  const filtered = filter === 'all' ? logs : logs.filter((l) => l.type === filter)

  if (loading) return <div className="text-text-muted animate-pulse">Chargement…</div>

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Logs d'activité</h1>
        <p className="text-text-secondary mt-1">{logs.length} événements récents</p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-6">
        {types.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={['px-3 py-1.5 rounded-lg text-sm transition-all', filter === t.key ? 'glass-btn-accent' : 'glass-btn'].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card p-10 text-center text-text-muted">Aucun événement</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((event, i) => {
            const cfg = TYPE_CONFIG[event.type] ?? { icon: '•', color: 'bg-white/[0.04] border-white/[0.08]' }
            return (
              <div key={event.id} className={['flex items-start gap-4 p-4 rounded-xl border', cfg.color].join(' ')}>
                <span className="text-xl shrink-0 mt-0.5">{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary text-sm font-medium leading-snug">{event.label}</p>
                  {event.sub && <p className="text-text-muted text-xs mt-0.5">{event.sub}</p>}
                </div>
                <span className="text-text-muted text-xs shrink-0 mt-0.5">{formatDate(event.createdAt)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
